import type { ScheduleGet } from 'c-sdk';

/**
 * The schedule list on the right rail: how fetched and saved schedules are combined. Used by
 * `useNewCampaignSchedule`, which keeps the list in shared state for both schedule page variants.
 */

/** The campaign Default first, every other schedule in the order the API sent it. */
export const sortSchedules = (schedules: ScheduleGet[]): ScheduleGet[] =>
    [...schedules].sort((a, b) => (b.is_default ? 1 : -1) - (a.is_default ? 1 : -1));

/**
 * A page of schedules from the API, keeping the Default on screen. The Default is not on every page
 * (or in every search), but the rail always shows it, so when a page lacks one the Default already
 * held is kept; a Default on the page replaces it.
 */
export const schedulesPageWithDefault = (
    current: ScheduleGet[],
    fetched: ScheduleGet[],
): ScheduleGet[] => {
    const fetchedDefault = fetched.find(schedule => schedule.is_default);
    const heldDefault = current.find(schedule => schedule.is_default);

    return sortSchedules(fetchedDefault || !heldDefault ? fetched : [...fetched, heldDefault]);
};

/** Saved schedules replace the ones with the same id, in place; new ones are added at the end. */
export const mergeSchedules = (current: ScheduleGet[], saved: ScheduleGet[]): ScheduleGet[] => {
    const merged = [...current];

    saved.forEach(schedule => {
        const index = current.findIndex(s => s.id === schedule.id);
        if (index === -1) merged.push(schedule);
        else merged[index] = schedule;
    });

    return merged;
};

/** The list without the given schedules. */
export const withoutSchedules = (current: ScheduleGet[], scheduleIds: number[]): ScheduleGet[] =>
    current.filter(s => !scheduleIds.includes(s.id));
